/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';
import { $isVariableNode } from '../../nodes/VariableNode';

/**
 * Plugin that handles atomic deletion of VariableNodes.
 * When backspace or delete is pressed and the selection is within or adjacent to a VariableNode,
 * the entire node is deleted as a single unit.
 */
export const VariableBackspacePlugin = (): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removeBackspaceHandler = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event: KeyboardEvent) => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return false;
        }

        // Only handle collapsed selections (cursor, not range selection)
        if (!selection.isCollapsed()) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();

        // If the cursor is inside a VariableNode, delete the entire node
        if ($isVariableNode(anchorNode)) {
          event.preventDefault();
          anchorNode.remove();
          return true;
        }

        // Check if cursor is at position 0 and previous sibling is a VariableNode
        const offset = selection.anchor.offset;
        if (offset === 0) {
          const prevSibling = anchorNode.getPreviousSibling();
          if ($isVariableNode(prevSibling)) {
            event.preventDefault();
            prevSibling.remove();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const removeDeleteHandler = editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event: KeyboardEvent) => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return false;
        }

        // Only handle collapsed selections (cursor, not range selection)
        if (!selection.isCollapsed()) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();

        // If the cursor is inside a VariableNode, delete the entire node
        if ($isVariableNode(anchorNode)) {
          event.preventDefault();
          anchorNode.remove();
          return true;
        }

        // Check if cursor is at end of text and next sibling is a VariableNode
        const textContent = anchorNode.getTextContent();
        const offset = selection.anchor.offset;
        if (offset === textContent.length) {
          const nextSibling = anchorNode.getNextSibling();
          if ($isVariableNode(nextSibling)) {
            event.preventDefault();
            nextSibling.remove();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      removeBackspaceHandler();
      removeDeleteHandler();
    };
  }, [editor]);

  return null;
};
